import { Link } from 'react-router-dom';
import './ForBusinessPage.css';

const STEPS = [
  {
    n: '01',
    title: 'Post your commercial scrap',
    body: 'Describe the load, share photos, and add your site postcode. Listings are free and quick to create for trade teams.',
  },
  {
    n: '02',
    title: 'Match licensed collectors',
    body: 'Verified scrap carriers review your job and agree a collection slot that fits your site access and working hours.',
  },
  {
    n: '03',
    title: 'Compliant clearance',
    body: 'Scrap is removed through licensed routes with documentation trails — supporting duty of care for your business.',
  },
];

const ITEMS = [
  { title: 'Trade & workshop scrap', body: 'Offcuts, fabrications, broken tools, and day-to-day metal waste from busy workshops.' },
  { title: 'Construction leftovers', body: 'Pipes, rebar, trunking, radiators, and site metal after fit-outs or strip-outs.' },
  { title: 'Retail & office clear-outs', body: 'Metal fixtures, shelving, broken equipment, and end-of-tenancy clearances.' },
  { title: 'Plant & equipment', body: 'Decommissioned machinery parts, frames, and heavy metal assets ready for collection.' },
  { title: 'Mixed commercial loads', body: 'Larger batches or recurring scrap streams from yards, depots, and storage units.' },
  { title: 'Landlord & property jobs', body: 'Void clearances, refurb scrap, and property-management metal removals.' },
];

const BENEFITS = [
  {
    title: 'Duty of care support',
    body: 'Work with licensed collectors and keep a clearer trail for responsible scrap handling.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M8 4.5h6l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-8.5A1.5 1.5 0 0 1 6.5 19V6A1.5 1.5 0 0 1 8 4.5z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path d="M14 4.8V9h4M9.5 13h5M9.5 16h3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Site-ready scheduling',
    body: 'Arrange collections around access windows, loading bays, and operational downtime.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="5" width="16" height="15" rx="2" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8 3.5V7M16 3.5V7M4 10h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    title: 'Licensed network',
    body: 'Avoid unlicensed tip runs — get matched with verified scrap collectors near your site.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M12 3.5l7 2.5v5.2c0 4.3-2.9 7.3-7 9.3-4.1-2-7-5-7-9.3V6L12 3.5z"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <path d="M9.2 12.1l1.9 1.9 3.7-3.9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    title: 'Scales with your jobs',
    body: 'Post one-off clearances or keep listing scrap as projects finish — free to post each time.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4 16l4-8 4 5 3-3 5 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 19h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
  },
];

const TIPS = [
  'Note site access, parking, gate codes, and safe loading points in your post.',
  'Estimate volume or weight where possible so collectors arrive with the right vehicle.',
  'Separate hazardous items and confirm scrap is metal-focused before listing.',
  'Ask collectors for transfer notes or evidence if your compliance process requires it.',
];

export default function ForBusinessPage() {
  return (
    <div className="business-page">
      <section className="business-hero" aria-labelledby="business-heading">
        <div className="business-hero-inner">
          <p className="business-kicker">SnapScrap for trade</p>
          <h1 id="business-heading">For Business</h1>
          <p>
            Clear commercial scrap without slowing your site down. Post trade metal for free and get
            matched with licensed collectors who understand business collections.
          </p>
          <div className="business-hero-actions">
            <Link className="business-btn" to="/post-scrap">
              Post Scrap →
            </Link>
            <Link className="business-btn business-btn-secondary" to="/contact">
              Talk to our team
            </Link>
          </div>
        </div>
      </section>

      <section className="business-section" aria-labelledby="business-steps-heading">
        <div className="business-inner">
          <header className="business-header">
            <h2 id="business-steps-heading">How it works for businesses</h2>
            <p>From listing to licensed clearance in three clear steps</p>
          </header>
          <ol className="business-steps">
            {STEPS.map((step) => (
              <li key={step.n}>
                <span className="business-step-n" aria-hidden="true">
                  {step.n}
                </span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="business-section business-section-alt" aria-labelledby="business-items-heading">
        <div className="business-inner">
          <header className="business-header">
            <h2 id="business-items-heading">What businesses usually post</h2>
            <p>Built for trade waste, site clearances, and recurring scrap streams</p>
          </header>
          <ul className="business-items">
            {ITEMS.map((item) => (
              <li key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="business-section" aria-labelledby="business-benefits-heading">
        <div className="business-inner">
          <header className="business-header">
            <h2 id="business-benefits-heading">Why businesses choose SnapScrap</h2>
            <p>Faster clearances with licensed handling and less admin friction</p>
          </header>
          <ul className="business-benefits">
            {BENEFITS.map((item) => (
              <li key={item.title}>
                <span className="business-benefit-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="business-section business-section-alt" aria-labelledby="business-tips-heading">
        <div className="business-inner business-tips-wrap">
          <header className="business-header">
            <h2 id="business-tips-heading">Tips for smoother trade collections</h2>
            <p>Help collectors quote accurately and turn up ready for your site</p>
          </header>
          <ul className="business-tips">
            {TIPS.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="business-cta-section" aria-label="Business scrap call to action">
        <div className="business-inner">
          <div className="business-cta">
            <h2>Need scrap cleared from your site?</h2>
            <p>Post your commercial scrap for free and get matched with a licensed collector.</p>
            <div className="business-hero-actions">
              <Link className="business-btn" to="/post-scrap">
                Post Scrap →
              </Link>
              <Link className="business-btn business-btn-secondary" to="/become-a-collector">
                Become a Collector
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
